import { Component, OnInit, AfterViewInit, OnDestroy, ViewChildren, ElementRef, QueryList, Inject, PLATFORM_ID } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { RouterLink } from '@angular/router';
import { LandingNavbarComponent } from '../landing-navbar/navbar.component';
import { CommonModule } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { Meta } from '@angular/platform-browser';

export interface Testimonial {
    image?: string;
    logo?: string;
    quote?: string;
    from?: string;
    readMoreUrl?: string;
}

type ScaleBreakpoint = {
    minLength: number;
    scale: number;
};
interface StackImage {
    src: string;
    angle: number;
    state: 'idle' | 'entering' | 'active' | 'exiting';
    zIndex: number;
}

@Component({
    selector: 'app-landing',
    standalone: true,
    imports: [TranslocoPipe, RouterLink, LandingNavbarComponent, CommonModule],
    templateUrl: './landing.component.html',
    styleUrls: ['./landing.component.css'],
    animations: [
        trigger('wordInOut', [
            state('in', style({ transform: 'translateY(0)', opacity: 1 })),
            state('out', style({ transform: 'translateY(-100%)', opacity: 0 })),
            transition('in => out', [animate('300ms ease-in')]),
            transition('out => in', [
                style({ transform: 'translateY(100%)', opacity: 0 }),
                animate('300ms ease-out'),
            ]),
        ])
    ]
})
export class LandingComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren('modelRefs', { read: ElementRef }) modelElements!: QueryList<ElementRef>;

    phrases: string[][] = [];

    currentIndex = 0;
    wordA = '';
    wordB = '';

    wordAState: 'in' | 'out' = 'in';
    wordBState: 'in' | 'out' = 'in';

    private intervalId: any;

    images: string[] = [
        '../../assets/images/landing/hero/landing-hero-1.png',
        '../../assets/images/landing/hero/landing-hero-2.jpg',
        '../../assets/images/landing/hero/landing-hero-3.jpg',
        '../../assets/images/landing/hero/landing-hero-4.jpg',
        '../../assets/images/landing/hero/landing-hero-5.jpg',
        '../../assets/images/landing/hero/landing-hero-6.jpg',
        '../../assets/images/landing/hero/landing-hero-7.png',
        '../../assets/images/landing/hero/landing-hero-8.jpg',
        '../../assets/images/landing/hero/landing-hero-9.jpg',
        '../../assets/images/landing/hero/landing-hero-10.jpg',
        '../../assets/images/landing/hero/landing-hero-11.png',
        '../../assets/images/landing/hero/landing-hero-12.jpg',
    ];

    stackImages: StackImage[] = [];
    private currentImageIndex = 0;
    private activeQueue: number[] =[];
    private zCounter = 0;
    private readonly MAX_ACTIVE = 8;
    private readonly ENTER_DURATION = 500;
    private readonly EXIT_DURATION = 300;
    private intervalImageId: any;

    angles: number[] = [-2, 0, 4, -3, 1, -1, 3, 0, 4, -2, 1, -3];

    activeModelIndex = 0;
    models: Array<{
        title: string;
        content: string;
        image: string;
    }> = [];
    readonly modelImages = [
        '../../assets/images/landing/under-hero-1.jpg',
        '../../assets/images/landing/under-hero-2.png',
        '../../assets/images/landing/under-hero-3.jpg',
    ]

    testimonials: Array<{ image: string; logo: string; quote: string; from: string; readMoreUrl: string; }> = [];

    testimonialImages = [
        {
            image: '/assets/images/landing/testimonials/testimonial-1.png',
            logo: '/assets/images/landing/testimonials/logos/radio-canada.png',
        },
        {
            image: '/assets/images/landing/testimonials/testimonial-2.jpg',
            logo: '/assets/images/landing/testimonials/logos/eater-montreal.svg',
        },
        {
            image: '/assets/images/landing/testimonials/testimonial-3.jpg',
            logo: '/assets/images/landing/testimonials/logos/government-of-canada.png',
        },
        {
            image: '/assets/images/landing/testimonials/testimonial-4.jpg',
            logo: '/assets/images/landing/testimonials/logos/metro.svg',
        },
        {
            image: '/assets/images/landing/testimonials/testimonial-5.jpg',
            logo: '/assets/images/landing/testimonials/logos/microsoft.png',
        },
    ]

    private intervalTestimonialId: any;
    currentTestimonialIndex: number = 0;

    constructor(
        public translate: TranslocoService,
        private meta: Meta,
        @Inject(PLATFORM_ID) private platformId: any
    ) {
        // transloco setup for cycling phrases
        this.translate
            .selectTranslateObject('landing.hero.phrases')
            .subscribe((phrases: string[][]) => {
                this.phrases = phrases;
                this.wordA = phrases[0][0];
                this.wordB = phrases[0][1];
            });
        // transloco setup for models
        this.translate
            .selectTranslateObject('landing.models')
            .subscribe((models: Array<{ title: string; content: string }>) => {
                this.models = models.map((model, i) => ({
                    ...model,
                    image: this.modelImages[i]
                }));
            });
        // transloco setup for testimonials
        this.translate
            .selectTranslateObject('landing.testimonials.list')
            .subscribe((testimonials: Array<{ quote: string; from: string; readMoreUrl: string; }>) => {
                this.testimonials = testimonials.map((testimonial, i) => ({
                    ...testimonial,
                    ...this.testimonialImages[i]
                }));
            })
    }

    get totalSlides() {
        return this.testimonials.length;
    }

    get prevTestimonialIndex() {
        return (this.currentTestimonialIndex - 1 + this.totalSlides) % this.totalSlides;
    }

    get nextTestimonialIndex() {
        return (this.currentTestimonialIndex + 1) % this.totalSlides;
    }

    get deliverySolutionsLink() {
        return this.translate.getActiveLang() == 'en' ? 'https://form.typeform.com/to/GQco8dwG?utm_source=radish_frontend' : 'https://form.typeform.com/to/ubaiFTuX?utm_source=radish_frontend'
    }

    nextSlide(manual = false) {
        this.currentTestimonialIndex = (this.currentTestimonialIndex + 1) % this.totalSlides;
        if (manual) {
            this.startTestimonialAutoPlay();
        }
    }

    prevSlide(manual = false) {
        this.currentTestimonialIndex = (this.currentTestimonialIndex - 1 + this.totalSlides) % this.totalSlides;
        if (manual) {
            this.startTestimonialAutoPlay();
        }
    }

    ngOnInit() {
        if (!isPlatformBrowser(this.platformId)) return;
        document.body.style.backgroundColor = '#fed1d2';
        // set up cycling text 
        this.intervalId = setInterval(() => this.nextPhrase(), 5000);
        // set up image stack
        this.stackImages = this.images.map((src, i) => ({
            src,
            angle: this.angles[i % this.angles.length],
            state: 'idle',
            zIndex: 0
        }));
        setTimeout(() => {
            this.nextImage();
            this.intervalImageId = setInterval(() => this.nextImage(), 3500);
        }, 1000);
        // set up testimonials
        this.startTestimonialAutoPlay();
    }

    ngAfterViewInit() {
        if (!isPlatformBrowser(this.platformId)) return;

        const options = {
            root: document.querySelector('.models-container'),
            threshold: 0.7
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const index = this.modelElements.toArray().findIndex(
                        el => el.nativeElement === entry.target
                    );
                    this.activeModelIndex = index;
                }
            });
        }, options);

        this.modelElements.forEach(el => observer.observe(el.nativeElement));
    }

    ngOnDestroy() {
        clearInterval(this.intervalId);
        clearInterval(this.intervalImageId);
        clearInterval(this.intervalTestimonialId);
        if (!isPlatformBrowser(this.platformId)) return;
        document.body.style.backgroundColor = '';
    }

    private startTestimonialAutoPlay() {
        if (this.intervalTestimonialId) {
            clearInterval(this.intervalTestimonialId);
        }

        this.intervalTestimonialId = setInterval(() => {
            this.nextSlide();
        }, 8000);
    }

    nextPhrase() {
        this.wordAState = 'out';
        setTimeout(() => {
            this.wordBState = 'out';
        }, 120);

        setTimeout(() => {
            this.currentIndex = (this.currentIndex + 1) % this.phrases.length;
            this.wordA = this.phrases[this.currentIndex][0];
            this.wordB = this.phrases[this.currentIndex][1];

            this.wordAState = 'in';
            setTimeout(() => (this.wordBState = 'in'), 120);
        }, 500);
    }


    nextImage() {
        if (this.activeQueue.length >= this.MAX_ACTIVE) {
            const oldestIdx = this.activeQueue.shift()!;
            const oldest = this.stackImages[oldestIdx];
            oldest.state = 'exiting';
            setTimeout(() => {
                oldest.state = 'idle';
            }, this.EXIT_DURATION);
        }

        const img = this.stackImages[this.currentImageIndex];
        img.zIndex = ++this.zCounter;
        img.state = 'entering';

        setTimeout(() => {
            img.state = 'active';
        }, this.ENTER_DURATION);
        
        this.activeQueue.push(this.currentImageIndex);
        this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
    }

    getTestimonialQuote(testimonial: string) {
        return this.translate.getActiveLang() == 'en' ? `"${testimonial}"` : `« ${testimonial}\xa0»`;
    }

    // breakpoints NEED to be in decreasing order of maxSize
    scaleLongText(text: string, breakpoints: readonly ScaleBreakpoint[]) {
        const n = text.length;
        for (const { minLength, scale } of breakpoints) {
            if (n > minLength) return scale;
        }
        return 1;
    }

    private readonly CTA_SCALE: readonly ScaleBreakpoint[] = [{ minLength: 20, scale: 0.75 }];
    getHeroCtaScale(t: string) {
        return this.scaleLongText(t, this.CTA_SCALE);
    }

    private readonly TESTIMONIAL_SCALE: readonly ScaleBreakpoint[] = [{ minLength: 100, scale: 0.6 }, { minLength: 100, scale: 0.8 }];
    getTestimonialScale(t: string) {
        return this.scaleLongText(t, this.TESTIMONIAL_SCALE);
    }

    openLink(url: string) {
        window.open(url, '_blank');
    }
}
